/**
 * NotificationsView — Employee notifications list
 * Shows all notifications with read/unread state and mark-as-read actions.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../../components/ui/button';
import axios from 'axios';
import {
  Bell, CheckCircle, Loader2, FileText, Calendar, AlertTriangle
} from 'lucide-react';
import config from 'config';

const API = `${config.API_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

const NOTIF_ICONS = {
  DOCUMENT_ADDED: FileText,
  PAYSLIP_ADDED: FileText,
  SCHEDULE_UPDATED: Calendar,
  LEAVE_APPROVED: CheckCircle,
  LEAVE_REJECTED: AlertTriangle,
};

const fmtDate = (iso) => {
  if (!iso) return '--';
  try { return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return '--'; }
};

export default function NotificationsView() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/employees/me/notifications`, { headers: getHeaders() });
      setNotifications(res.data?.notifications || []);
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const markAsRead = async (id) => {
    try {
      await axios.patch(`${API}/employees/me/notifications/${id}/read`, {}, { headers: getHeaders() });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch { /* silent */ }
  };

  const markAllRead = async () => {
    setMarkingAll(true);
    try {
      await axios.patch(`${API}/employees/me/notifications/read-all`, {}, { headers: getHeaders() });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch { /* silent */ }
    setMarkingAll(false);
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-[#C9A24D] animate-spin" /></div>;

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="space-y-3" data-testid="notifications-list">
      {unreadCount > 0 && (
        <div className="flex items-center justify-between">
          <span className="text-white/40 text-xs">{unreadCount} non lue{unreadCount > 1 ? 's' : ''}</span>
          <Button variant="ghost" size="sm" onClick={markAllRead} disabled={markingAll}
            className="text-[#C9A24D] text-xs h-8" data-testid="mark-all-read-btn">
            {markingAll ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
            Tout marquer lu
          </Button>
        </div>
      )}

      {notifications.length === 0 ? (
        <div className="text-center py-12 text-white/30">
          <Bell className="w-8 h-8 mx-auto mb-2" />
          <p className="text-sm">Aucune notification</p>
        </div>
      ) : (
        notifications.map(notif => {
          const Icon = NOTIF_ICONS[notif.type] || Bell;
          return (
            <button
              key={notif.id}
              onClick={() => !notif.is_read && markAsRead(notif.id)}
              className={`w-full text-left p-3 rounded-xl border transition-colors ${
                notif.is_read
                  ? 'bg-white/[0.02] border-white/5'
                  : 'bg-[#C9A24D]/5 border-[#C9A24D]/15 hover:bg-[#C9A24D]/8'
              }`}
              data-testid={`notification-${notif.id}`}
            >
              <div className="flex items-start gap-2.5">
                <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${notif.is_read ? 'text-white/20' : 'text-[#C9A24D]'}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-xs ${notif.is_read ? 'text-white/40' : 'text-white'}`}>{notif.message}</p>
                  <p className="text-white/20 text-[10px] mt-1">{fmtDate(notif.created_at)}</p>
                </div>
                {!notif.is_read && <span className="w-2 h-2 rounded-full bg-[#C9A24D] mt-1.5 shrink-0" />}
              </div>
            </button>
          );
        })
      )}
    </div>
  );
}
